import posthog from 'posthog-js';

// List of critical events that require validation
const CRITICAL_EVENTS = ['purchase.completed', 'checkout.started', 'user.registered'];

/**
 * trackEvent
 * A helper to send events to PostHog with consistent naming and properties.
 * Enhanced with validation for critical events and duplicate prevention.
 *
 * @param {string} eventName - Name of the event to track
 * @param {Object} properties - Properties to include with the event
 * @param {boolean} force - Force tracking even if validation fails (use sparingly)
 * @returns {boolean} - Whether the event was successfully tracked
 */
export function trackEvent(eventName, properties = {}, force = false) {
  if (!posthog) return false;

  try {
    // For critical events, ensure they have required properties
    if (CRITICAL_EVENTS.includes(eventName) && !force) {
      // For purchase events, validate transaction data
      if (eventName === 'purchase.completed') {
        if (
          !properties.transaction_id ||
          !properties.total_amount ||
          !properties.services_purchased
        ) {
          console.warn(`[Analytics] Incomplete data for ${eventName} event`, properties);
          return false;
        }
      }
    }

    // Add common properties to all events
    const enhancedProps = {
      ...properties,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      path: window.location.pathname,
    };

    posthog.capture(eventName, enhancedProps);

    // For debugging in development
    if (import.meta.env.DEV) {
      console.log(`[Analytics] Tracked: ${eventName}`, enhancedProps);
    }

    return true;
  } catch (error) {
    console.error(`[Analytics] Failed to track ${eventName}:`, error);
    return false;
  }
}

/**
 * Hash data using SHA-256 for Facebook Conversions API
 * @param {string} data - Data to hash
 * @returns {string|Promise<string>} - Hashed data or null if error
 */
// Hash function removed - was only used for Facebook tracking

/**
 * Fallback hash function for environments without crypto.subtle
 * Simple but less secure hashing alternative
 * @param {string} data - Data to hash
 * @returns {string} - Simple hash representation
 */
// Fallback hash function removed - was only used for Facebook tracking

/**
 * Send event to Facebook Conversions API
 * @param {string} eventName - Standard Facebook event name (e.g., 'Purchase')
 * @param {Object} properties - Event properties
 * @returns {Promise<boolean>} - Whether the event was successfully sent
 */
// Facebook conversion tracking has been removed
